import { db } from '../../config/firebaseConfig';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';

/*
 * ICE yapılandırması.
 *
 * TURN kimlik bilgileri KODA GÖMÜLMEZ - eski koddaki ücretsiz Metered hesabı
 * süresi dolduğu için kimlik doğrulaması reddediliyor, hiç relay adayı
 * üretilemiyordu ve farklı ağlardaki kullanıcılar "bağlandı" görünüp
 * birbirini duyamıyordu. Yapılandırma .env'den gelir:
 *
 * 1) REACT_APP_METERED_DOMAIN + REACT_APP_METERED_API_KEY:
 *    Taze, süreli TURN kimlikleri her aramada Metered API'den çekilir
 *    (önerilen yol; kimlikler kendiliğinden yenilenir).
 *    Ücretsiz hesap: https://www.metered.ca/stun-turn
 * 2) REACT_APP_TURN_URLS (virgülle ayrık) + REACT_APP_TURN_USERNAME +
 *    REACT_APP_TURN_CREDENTIAL: sabit TURN sunucusu (ör. kendi coturn'unuz).
 * 3) Hiçbiri yoksa yalnızca STUN kullanılır - aynı ağda çalışır ama farklı
 *    ağlardaki kullanıcılar büyük olasılıkla bağlanamaz (konsola uyarı düşer).
 */
const STUN_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const TURN_CREDENTIAL_TTL_MS = 60 * 60 * 1000;
let cachedIceServers = null;
let cachedIceServersAt = 0;

const staticTurnFromEnv = () => {
  const urls = (process.env.REACT_APP_TURN_URLS || '')
    .split(',')
    .map((u) => u.trim())
    .filter(Boolean);
  if (urls.length === 0) return null;
  return [{
    urls,
    username: process.env.REACT_APP_TURN_USERNAME || undefined,
    credential: process.env.REACT_APP_TURN_CREDENTIAL || undefined,
  }];
};

const getIceServers = async () => {
  const domain = process.env.REACT_APP_METERED_DOMAIN;
  const apiKey = process.env.REACT_APP_METERED_API_KEY;

  if (domain && apiKey) {
    if (cachedIceServers && Date.now() - cachedIceServersAt < TURN_CREDENTIAL_TTL_MS) {
      return cachedIceServers;
    }
    try {
      const res = await fetch(
        `https://${domain}/api/v1/turn/credentials?apiKey=${encodeURIComponent(apiKey)}`
      );
      if (res.ok) {
        const turnServers = await res.json();
        cachedIceServers = [...STUN_SERVERS, ...turnServers];
        cachedIceServersAt = Date.now();
        return cachedIceServers;
      }
      console.error('TURN kimlikleri alınamadı (HTTP ' + res.status + '); STUN ile devam ediliyor.');
    } catch (error) {
      console.error('TURN kimlik isteği başarısız; STUN ile devam ediliyor:', error);
    }
  }

  const staticTurn = staticTurnFromEnv();
  if (staticTurn) return [...STUN_SERVERS, ...staticTurn];

  if (!getIceServers.infoLogged) {
    getIceServers.infoLogged = true;
    console.info(
      'Arama STUN ile (TURN olmadan) çalışıyor - tercih edilen yapılandırma bu. ' +
      'Farklı ağlardaki iki kullanıcı bağlanamazsa nedeni budur.'
    );
  }
  return STUN_SERVERS;
};

class WebRTCService {
  constructor() {
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.callId = null;
    this.unsubscribe = null;
    this.onRemoteTrack = null; // Callback for when remote track is added
    this.onConnectionFailed = null; // Bağlantı 'failed' olduğunda UI'yi uyarır
    this.hasRelayCandidate = false;
    // Bu uçta arayan mıyız aranan mı ('caller' | 'receiver'). ICE adaylarının
    // hangi alt koleksiyona yazılacağı buna göre belirlenir; asla Firestore
    // okumasıyla tahmin edilmez (aşağıdaki addIceCandidate açıklamasına bak).
    this.role = null;
    // Aday dinleyicileri; arama bitince kapatılır (eskiden hiç kapatılmıyor,
    // sonraki aramalara sızıyordu).
    this.candidateUnsubs = [];
  }

  // Initialize peer connection
  async createPeerConnection() {
    const servers = await getIceServers();
    this.peerConnection = new RTCPeerConnection({
      iceServers: servers,
      iceCandidatePoolSize: 10,
    });
    this.remoteStream = new MediaStream();
    this.hasRelayCandidate = false;

    // TURN yapılandırılmışsa ama relay adayı gelmiyorsa (erişilemiyor /
    // kimlik geçersiz) bunu aramanın ilk saniyelerinde teşhis için bekçi.
    // TURN bilerek kapalıyken sessiz kalır.
    const turnConfigured = servers.some((srv) =>
      [].concat(srv.urls || []).some((u) => String(u).startsWith('turn'))
    );
    if (turnConfigured) {
      setTimeout(() => {
        if (this.peerConnection && !this.hasRelayCandidate) {
          console.warn(
            'Hiç TURN relay adayı üretilemedi. TURN sunucusuna erişilemiyor ya da ' +
            'kimlik bilgileri geçersiz; farklı ağlar arasında bağlantı kurulamayabilir.'
          );
        }
      }, 8000);
    }

    // Add local tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        console.log('Adding local track to peer connection:', track.kind);
        this.peerConnection.addTrack(track, this.localStream);
      });
    }

    // Listen for remote tracks
    this.peerConnection.ontrack = (event) => {
      console.log('Remote track received:', event.track.kind, event.track.readyState);
      event.streams[0].getTracks().forEach(track => {
        console.log('Adding remote track to stream:', track.kind);
        this.remoteStream.addTrack(track);
      });

      // Notify listener that remote track was added
      if (this.onRemoteTrack) {
        this.onRemoteTrack(this.remoteStream);
      }
    };

    // Listen for ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.callId) {
        console.log('ICE candidate generated:', event.candidate.type, event.candidate.protocol);
        if (event.candidate.type === 'relay') {
          this.hasRelayCandidate = true;
        }
        this.addIceCandidate(event.candidate);
      }
    };

    // Log ICE gathering state
    this.peerConnection.onicegatheringstatechange = () => {
      console.log('ICE gathering state:', this.peerConnection.iceGatheringState);
    };

    // Log connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      console.log('Connection state:', this.peerConnection.connectionState);
      if (this.peerConnection.connectionState === 'failed') {
        console.error('WebRTC connection failed - this may be due to network/firewall issues');
        if (this.onConnectionFailed) {
          this.onConnectionFailed();
        }
      }
    };

    // Log ICE connection state
    this.peerConnection.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', this.peerConnection.iceConnectionState);
      // If ICE fails, try to restart
      if (this.peerConnection.iceConnectionState === 'failed') {
        console.log('ICE connection failed, attempting restart...');
        this.peerConnection.restartIce();
      }
    };

    // Log signaling state changes
    this.peerConnection.onsignalingstatechange = () => {
      console.log('Signaling state:', this.peerConnection.signalingState);
    };

    return this.peerConnection;
  }

  // Get user media (camera and microphone)
  async getUserMedia(videoEnabled = true, audioEnabled = true) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoEnabled ? { width: 1280, height: 720 } : false,
        audio: audioEnabled
      });
      this.localStream = stream;
      return stream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      throw error;
    }
  }

  // Create a new call
  async createCall(callerId, callerName, receiverId, receiverName) {
    try {
      // Create call document
      const callRef = doc(collection(db, 'calls'));
      this.callId = callRef.id;
      this.role = 'caller';

      const callData = {
        callerId,
        callerName,
        receiverId,
        receiverName,
        status: 'ringing',
        createdAt: serverTimestamp(),
        type: 'video'
      };

      await setDoc(callRef, callData);

      // Create peer connection
      await this.createPeerConnection();

      // Create offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Save offer to Firestore
      await updateDoc(callRef, {
        offer: {
          type: offer.type,
          sdp: offer.sdp
        }
      });

      // Listen for answer
      this.listenForAnswer();

      return this.callId;
    } catch (error) {
      console.error('Error creating call:', error);
      throw error;
    }
  }

  // Answer a call
  async answerCall(callId) {
    try {
      this.callId = callId;
      this.role = 'receiver';
      const callRef = doc(db, 'calls', callId);
      const callSnap = await getDoc(callRef);

      if (!callSnap.exists()) {
        throw new Error('Call not found');
      }

      const callData = callSnap.data();

      // Create peer connection
      await this.createPeerConnection();

      // Set remote description from offer
      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription(callData.offer)
      );

      // Create answer
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      // Save answer to Firestore
      await updateDoc(callRef, {
        answer: {
          type: answer.type,
          sdp: answer.sdp
        },
        status: 'connected'
      });

      // Listen for ICE candidates
      this.listenForIceCandidates('callerCandidates');

      return callData;
    } catch (error) {
      console.error('Error answering call:', error);
      throw error;
    }
  }

  // Listen for answer from receiver
  listenForAnswer() {
    const callRef = doc(db, 'calls', this.callId);

    this.unsubscribe = onSnapshot(callRef, async (snapshot) => {
      const data = snapshot.data();

      // Check if peer connection still exists before accessing it
      if (data?.answer && this.peerConnection && !this.peerConnection.currentRemoteDescription) {
        const answer = new RTCSessionDescription(data.answer);
        await this.peerConnection.setRemoteDescription(answer);

        // Listen for ICE candidates
        this.listenForIceCandidates('receiverCandidates');
      }
    });
  }

  // Add ICE candidate to Firestore
  async addIceCandidate(candidate) {
    try {
      // Aday, bu ucun rolüne göre yazılır. Eskiden rol her adayda call
      // belgesi okunup "answer var mı?" diye bakılarak tahmin ediliyordu;
      // cevap henüz Firestore'a yüklenmeden üretilen ALICI adayları
      // callerCandidates'e, cevaptan sonra üretilen ARAYAN adayları (geç
      // gelen TURN relay adayları dahil) receiverCandidates'e düşüyordu.
      // İki taraf da birbirinin adaylarını alamadığı için arama "bağlandı"
      // görünüp medya akmıyordu — özellikle farklı ağlardaki bilgisayarlarda.
      const candidateCollection =
        this.role === 'receiver' ? 'receiverCandidates' : 'callerCandidates';

      const candidatesRef = collection(db, 'calls', this.callId, candidateCollection);
      await addDoc(candidatesRef, candidate.toJSON());
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  }

  // Listen for ICE candidates
  listenForIceCandidates(candidateCollection) {
    const candidatesRef = collection(db, 'calls', this.callId, candidateCollection);

    const unsubscribe = onSnapshot(candidatesRef, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const candidateData = change.doc.data();
          const candidate = new RTCIceCandidate(candidateData);
          try {
            // Check if peer connection still exists before adding candidate
            if (this.peerConnection) {
              await this.peerConnection.addIceCandidate(candidate);
            }
          } catch (error) {
            console.error('Error adding ICE candidate:', error);
          }
        }
      });
    });
    this.candidateUnsubs.push(unsubscribe);
  }

  // End call
  async endCall() {
    try {
      console.log('Ending call...');

      // Close peer connection
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      // Stop local stream
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => track.stop());
        this.localStream = null;
      }

      // Update call status in Firestore
      if (this.callId) {
        const callRef = doc(db, 'calls', this.callId);
        try {
          await updateDoc(callRef, {
            status: 'ended',
            endedAt: serverTimestamp()
          });
        } catch (error) {
          console.log('Call document may already be deleted');
        }

        // Clean up call document after 30 seconds
        setTimeout(async () => {
          try {
            await deleteDoc(callRef);
          } catch (error) {
            console.log('Call document cleanup - already deleted');
          }
        }, 30000);
      }

      // Unsubscribe from listeners
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      this.candidateUnsubs.forEach((unsub) => {
        try { unsub(); } catch (e) { /* zaten kapalı */ }
      });
      this.candidateUnsubs = [];

      // Clear callback
      this.onRemoteTrack = null;
      this.onConnectionFailed = null;

      this.callId = null;
      this.role = null;
      this.remoteStream = null;

      console.log('Call ended successfully');
    } catch (error) {
      console.error('Error ending call:', error);
      throw error;
    }
  }

  // Reject call
  async rejectCall(callId) {
    try {
      const callRef = doc(db, 'calls', callId);
      await updateDoc(callRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp()
      });

      // Clean up call document after 5 seconds
      setTimeout(async () => {
        try {
          await deleteDoc(callRef);
        } catch (error) {
          console.error('Error deleting call document:', error);
        }
      }, 5000);
    } catch (error) {
      console.error('Error rejecting call:', error);
      throw error;
    }
  }

  // Toggle mute
  toggleMute() {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        return !audioTrack.enabled; // Return muted state
      }
    }
    return false;
  }

  // Toggle video
  toggleVideo() {
    if (this.localStream) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        return !videoTrack.enabled; // Return video off state
      }
    }
    return false;
  }

  // Get call by ID
  async getCall(callId) {
    try {
      const callRef = doc(db, 'calls', callId);
      const callSnap = await getDoc(callRef);

      if (callSnap.exists()) {
        return { id: callSnap.id, ...callSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting call:', error);
      return null;
    }
  }
}

export default new WebRTCService();
